/* eslint-disable no-magic-numbers -- surface tokens are named by number */
import React from 'react';
import styled from 'styled-components';
import { Icon } from '../icon';
import { Loader } from '../loader';
import { mainTheme } from '../themes/mainTheme';

// Square with a 2px radius, like every team and member picture.
const RADIUS = 2;

export type ImagePickerProps = {
	/** The image to show: a URL or a `data:` URI. Empty = nothing picked. */
	value?: string | null,
	/** A new `data:` URI when a file is picked, `null` when the shown image is cleared. */
	onChange: (image: string | null) => void,
	/** Side length in px. Without it the picker fills its parent. */
	size?: number,
	/** Accessible names for the two states. */
	uploadLabel: string,
	removeLabel: string,
	id?: string,
};

// One element, so a parent that positions its children (`> *`) moves the input with the slot.
const Root = styled.div<{ $size?: number }>`
	position: relative;
	flex: none;
	width: ${({ $size }) => ($size ? `${$size}px` : '100%')};
	height: ${({ $size }) => ($size ? `${$size}px` : '100%')};
`;

const Slot = styled.div<{ $image?: string, $loading: boolean }>`
	width: 100%;
	height: 100%;
	border-radius: ${RADIUS}px;
	overflow: hidden;
	background-color: ${mainTheme.surface[4]};
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	cursor: pointer;

	// The icon sits on a picture as often as on the empty box, so it takes neither's contrast.
	${Icon.cssSelector} {
		color: ${mainTheme.text.primary};
	}

	// With a picture the icon is the clear affordance, revealed on hover over the dimmed picture.
	${({ $image }) => $image && `
		&:before {
			content: '';
			background-image: url(${$image});
			background-size: cover;
			background-position: 50% 50%;
			position: absolute;
			inset: 0;
		}

		${Icon.cssSelector} { display: none; }

		&:hover {
			&:before { opacity: 0.3; }
			${Icon.cssSelector} { display: block; }
		}
	`}

	// Empty, the box lightens. fill.dimmed is a translucent overlay, so it is layered over the surface.
	${({ $image, $loading }) => !$image && !$loading && `
		&:hover, &:focus-visible {
			background-image: linear-gradient(${mainTheme.fill.dimmed}, ${mainTheme.fill.dimmed});
		}
	`}

	${({ $loading }) => $loading && `
		&:before { opacity: 0.3; }
	`}
`;

// Doubled class: livestats' global `input[type="file"] { display: block }` outranks a single class.
const HiddenFileInput = styled.input`
	&& {
		display: none;
	}
`;

const readAsDataUrl = (file: Blob) => new Promise<string>((resolve, reject) => {
	const reader = new FileReader();
	reader.onload = () => resolve(reader.result as string);
	reader.onerror = reject;
	reader.readAsDataURL(file);
});

/** Click to upload an image; once there is one, clicking clears it. The file comes back as a `data:` URI. */
export function ImagePicker({ value, onChange, size, uploadLabel, removeLabel, id }: ImagePickerProps) {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const [loading, setLoading] = React.useState(false);
	const image = value || undefined;

	const activate = () => (image ? onChange(null) : inputRef.current?.click());

	const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		// Picking the same file twice must fire `change` again.
		event.target.value = '';
		if (!file) {
			return;
		}
		setLoading(true);
		try {
			onChange(await readAsDataUrl(file));
		} finally {
			setLoading(false);
		}
	};

	return <Root $size={size}>
		<Slot
			$image={image}
			$loading={loading}
			role="button"
			tabIndex={0}
			aria-label={image ? removeLabel : uploadLabel}
			onClick={activate}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					activate();
				}
			}}
		>
			{loading ? <Loader size="sm" /> : <Icon icon={image ? 'x' : 'upload'} />}
		</Slot>
		<HiddenFileInput
			ref={inputRef}
			type="file"
			id={id}
			onChange={onFile}
			accept="image/png, image/jpeg, image/mpo, image/gif, image/bmp, image/webp"
		/>
	</Root>;
}
